// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, IconButton, Drawer, List, ListItem, ListItemText, Menu, MenuItem, Button, Box, Badge, useScrollTrigger } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useAuth } from '../AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import './Header.css';
import { initializeGoogleSignIn } from './auth/AuthUtils';
import { useStackGeneration } from '../contexts/StackGenerationContext';
import config from '../config';
import Mark from './landing/Mark';
import Wordmark from './landing/Wordmark';
// Brand tokens (May-'26 green/Archivo system). The header is the first shared
// app surface on the new tokens — see docs/landing-page-redesign/IMPLEMENTATION.md.
import { LANDING_FONT, LC, MAXW, r } from './landing/landingTokens';
function Header() {
  const { currentUser, logout } = useAuth();
  // A generating (accent dot inside a pulsing halo) or finished-and-unviewed
  // (bare accent dot) stack lights a dot on the desktop Home item; tapping it
  // returns the user to /create-stack — the live progress view while in
  // progress, the result once ready (mirrors the bottom nav).
  const { isReady: stackReady, isInProgress: stackInProgress } = useStackGeneration();
  const stackActive = stackInProgress || stackReady;
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [companyAnchorEl, setCompanyAnchorEl] = useState(null);
  const [libraryAnchorEl, setLibraryAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [message, setMessage] = useState('');
  const showAffiliateStore = config.FEATURES.AFFILIATE_STORE;
  const [appPlatform, setAppPlatform] = useState('web'); // 'web', 'ios', 'android'

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  // On the logged-out landing the bar starts transparent so the hero gradient
  // bleeds behind it, then solidifies on scroll. Everywhere else: solid.
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 16 });
  const isPublicLanding = location.pathname === '/' && !currentUser;
  const transparent = isPublicLanding && !scrolled;

  // Light-card dropdown styling, matched to the brand doc's actual component
  // rules rather than a generic MUI menu: Archivo, sentence case, ink idle /
  // signal-accent hover (the brief's one link color), a hairline card with the
  // shadow reserved for floating elements, comfortable 44px-ish touch targets.
  const menuPaperProps = {
    sx: {
      // surfaceAlt (not surface/white) — a faint green-tinted off-white ties
      // the panel to the palette instead of popping as a stark white rectangle
      // against the saturated evergreen bar; surfaceTint still reads as a
      // distinct hover step up from it.
      backgroundColor: LC.surfaceAlt,
      border: `${r(1)} solid ${LC.border}`,
      borderRadius: r(14),
      boxShadow: '0 1rem 2.5rem -0.75rem rgba(12, 31, 23, 0.35)',
      mt: 0.75,
      minWidth: r(180),
      '& .MuiList-root': { p: r(6) },
      '& .MuiMenuItem-root': {
        fontFamily: LANDING_FONT,
        // Match the 14px nav-button trigger — a dropdown shouldn't read
        // larger than the label that opens it.
        fontSize: '0.875rem',
        fontWeight: 600,
        color: LC.ink,
        borderRadius: r(8),
        px: r(14),
        py: r(11),
        '&:hover': { backgroundColor: LC.surfaceTint, color: LC.accent },
      },
    },
  };

  // Shared brand-link treatment for the desktop nav groups: mint text that
  // turns white on hover with a soft square-ish backdrop (no pill radii — flat
  // brand system). Applied to both the centered nav group and the right-hand
  // auth group so they style identically.
  const navGroupSx = {
    display: 'flex',
    alignItems: 'center',
    gap: { md: 0.5, lg: 1 },
    '& .MuiButton-root': {
      borderRadius: r(8),
      px: '0.75rem',
      fontFamily: LANDING_FONT,
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      color: LC.navLink,
    },
    '& .MuiButton-root:hover, & .MuiIconButton-root:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      color: 'primary.contrastText',
    },
  };

  useEffect(() => {
    // Initialize Google Sign-in handler
    initializeGoogleSignIn(navigate, setMessage);

    // --- Check running environment ---
    let platform = 'web';
    // Check for iOS
    if (window.webkit?.messageHandlers?.iOSInterface) {
        platform = 'ios';
    }
    // Check for Android (using the example name "AndroidInterface")
    else if (window.AndroidInterface) {
         platform = 'android';
    }

    setAppPlatform(platform);
    // ------------------------------------

  }, [navigate]);

  // Define a global function to check if the user is logged in
  window.checkUserLoggedIn = () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      // User is logged in, return true
      return JSON.stringify({ isLoggedIn: true });
    } else {
      // User is not logged in, return false
      return JSON.stringify({ isLoggedIn: false });
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCompanyMenu = (event) => {
    setCompanyAnchorEl(event.currentTarget);
  };

  const handleCompanyClose = () => {
    setCompanyAnchorEl(null);
  };

  const handleLibraryMenu = (event) => {
    setLibraryAnchorEl(event.currentTarget);
  };

  const handleLibraryClose = () => {
    setLibraryAnchorEl(null);
  };

  const handleLogin = () => {
    handleClose();
    navigate('/login');
  };

  const handleLogout = async () => {
    try {
      await logout(); // Wait for logout to complete
      handleClose();
      navigate('/'); // Now navigate to landing page
    } catch (error) {
      console.error("Logout failed:", error);
      // Still close menu and navigate away even if logout had an error
      handleClose();
      navigate('/');
    }
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleSupport = () => {
    handleClose();
    navigate('/contact-us');
  };

  const handleStacksClick = () => {
    console.log('🔍 Header - handleStacksClick called');
    console.log('🔍 Header - currentUser:', currentUser ? currentUser.uid : 'null');

    // If a stack is generating or freshly finished, take the user to it.
    if (stackActive) {
      navigate('/create-stack');
      return;
    }

    // Use replace to avoid any redirect loops
    if (currentUser) {
      console.log('🔍 Header - User logged in, navigating to /dashboard with replace');
      navigate('/dashboard', { replace: true });
    } else {
      console.log('🔍 Header - No user, navigating to / with replace');
      navigate('/', { replace: true });
    }
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  // Phone menu. Supplements and Conditions live in the bottom tab bar on
  // phones, so the Library's third entry, Interactions, is listed here instead
  // (STACK-595), for signed-in and signed-out visitors alike.
  const drawer = (
    <div
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {currentUser && (
          <>
            <ListItem button onClick={handleProfile}>
              <ListItemText primary="Profile" />
            </ListItem>
            <ListItem button onClick={() => navigate('/supplement-interaction-checker')}>
              <ListItemText primary="Interactions" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Approach" sx={{ fontWeight: 'bold' }} />
            </ListItem>
            <ListItem button onClick={() => navigate('/about-us')} sx={{ pl: 4 }}>
              <ListItemText primary="Our Story" />
            </ListItem>
            <ListItem button onClick={() => navigate('/blog')} sx={{ pl: 4 }}>
              <ListItemText primary="Insights" />
            </ListItem>
            <ListItem button onClick={handleSupport}>
              <ListItemText primary="Contact Us" />
            </ListItem>
            <ListItem button onClick={handleLogout}>
              <ListItemText primary="Log Out" />
            </ListItem>
          </>
        )}

        {!currentUser && (
          <>
            <ListItem button onClick={() => navigate('/supplement-interaction-checker')}>
              <ListItemText primary="Interactions" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Approach" sx={{ fontWeight: 'bold' }} />
            </ListItem>
            <ListItem button onClick={() => navigate('/about-us')} sx={{ pl: 4 }}>
              <ListItemText primary="Our Story" />
            </ListItem>
            <ListItem button onClick={() => navigate('/blog')} sx={{ pl: 4 }}>
              <ListItemText primary="Insights" />
            </ListItem>
            <ListItem button onClick={handleSupport}>
              <ListItemText primary="Contact Us" />
            </ListItem>
            <ListItem button onClick={handleLogin}>
              <ListItemText>Log in</ListItemText>
            </ListItem>
            <ListItem>
              <Button
                fullWidth
                onClick={() => navigate('/register')}
                sx={{
                  backgroundColor: LC.accent,
                  color: LC.accentInk,
                  fontFamily: LANDING_FONT,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: r(11),
                  py: '0.625rem',
                  '&:hover': { backgroundColor: LC.accentHover },
                }}
              >
                Start free trial
              </Button>
            </ListItem>
          </>
        )}
      </List>
    </div>
  );

  const logo = (
    <Box
      onClick={handleStacksClick}
      sx={{
        // flex-basis 0 so this and the right auth group grow from zero by the
        // same amount — that makes the two side groups equal width and the
        // center nav truly centered regardless of their content widths.
        flex: '1 1 0',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
      }}
    >
      <Mark size={24} variant="onDark" strokeWidth={3.5} intro />
      <Wordmark size={21} variant="onDark" />
    </Box>
  );

  return (
    // Flat, full-width evergreen bar (brand system — no curves, no floating
    // pill). Content clearance lives in App.js (paddingTop); the logged-out
    // landing is exempt so its hero bleeds up behind the transparent bar.
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: 0,
        boxShadow: 'none',
        backgroundImage: 'none',
        backgroundColor: transparent ? 'transparent' : LC.evergreen,
        borderBottom: `${r(1)} solid ${transparent ? 'transparent' : 'rgba(255, 255, 255, 0.1)'}`,
        transition: 'background-color 0.25s ease, border-color 0.25s ease',
        pt: 'env(safe-area-inset-top)',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          width: '100%',
          maxWidth: MAXW,
          mx: 'auto',
          px: { xs: '0.75rem', md: '1.25rem' },
          minHeight: { xs: '3.25rem', md: '3.5rem' },
          color: 'primary.contrastText',
        }}
      >
        {isMobile ? (
          <>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="left"
              open={drawerOpen}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  backgroundColor: LC.evergreen,
                  color: LC.navLink,
                  width: '78vw',
                  maxWidth: r(320),
                  pt: '0.5rem',
                  '& .MuiListItemText-primary': {
                    fontFamily: LANDING_FONT,
                    fontSize: '0.9375rem',
                    color: LC.navLink,
                  },
                  '& .MuiListItem-root:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                },
              }}
            >
              {drawer}
            </Drawer>
            {logo}
          </>
        ) : (
          <>
            {logo}
            {/* Center nav group — Home, Library, Market, Approach. The logo
                (left) and the auth group (right) both flex-grow equally, so
                this group sits truly centered between them. */}
            <Box sx={navGroupSx}>
            <Badge
              variant="dot"
              // Both states are the signal accent (D-8) — `success` resolves to
              // it. The amber this replaced was a risk colour used
              // decoratively, which the brief forbids outright.
              color="success"
              invisible={!stackActive}
              sx={{
                '& .MuiBadge-badge': {
                  top: '0.5rem',
                  right: '0.5rem',
                  // Generating: a halo ring around the dot that pulses
                  // outward. Ready: the bare dot.
                  //
                  // The ring is painted at rest as well as animated, which is
                  // the point. The brief requires looped motion to halt under
                  // `prefers-reduced-motion` (§9) AND requires status
                  // indicators to encode on more than hue (§9, WCAG 1.4.1).
                  // Motion alone satisfies neither together: halt the pulse
                  // and two identically-coloured dots become indistinguishable.
                  // Ring-vs-no-ring is a shape difference that survives the
                  // halt and works with colour ignored entirely.
                  //
                  // The keyframe name is header-specific on purpose. Emotion
                  // emits `@keyframes` under the literal name it is given, and
                  // keyframe names are GLOBAL in CSS — so a shared name across
                  // Header and BottomNav would mean the last one injected wins
                  // for both. That was harmless while the two definitions were
                  // byte-identical; these two are not (BottomNav composites a
                  // paper separator layer this one has no need for). Keep the
                  // names distinct.
                  ...(stackInProgress && {
                    boxShadow: `0 0 0 ${r(3)} ${alpha(theme.palette.success.main, 0.3)}`,
                    animation: 'stackGenPulseHeader 1.5s ease-in-out infinite',
                    '@keyframes stackGenPulseHeader': {
                      '0%, 100%': {
                        boxShadow: `0 0 0 ${r(2)} ${alpha(theme.palette.success.main, 0.4)}`,
                      },
                      '50%': {
                        boxShadow: `0 0 0 ${r(6)} ${alpha(theme.palette.success.main, 0)}`,
                      },
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                    },
                  }),
                },
              }}
            >
              <Button color="inherit" onClick={handleStacksClick}>
                Home
              </Button>
            </Badge>
            <Button
              color="inherit"
              onClick={handleLibraryMenu}
              endIcon={<ArrowDropDownIcon />}
              aria-controls="library-menu"
              aria-haspopup="true"
            >
              Library
            </Button>
            {showAffiliateStore && <Button color="inherit" href="/products">Market</Button>}
            <Button
              color="inherit"
              onClick={handleCompanyMenu}
              endIcon={<ArrowDropDownIcon />}
              aria-controls="company-menu"
              aria-haspopup="true"
            >
              Approach
            </Button>
            <Menu
              id="company-menu"
              PaperProps={menuPaperProps}
              anchorEl={companyAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(companyAnchorEl)}
              onClose={handleCompanyClose}
            >
              <MenuItem onClick={() => { handleCompanyClose(); navigate('/about-us'); }}>Our Story</MenuItem>
              <MenuItem onClick={() => { handleCompanyClose(); navigate('/blog'); }}>Insights</MenuItem>
            </Menu>
            <Menu
              id="library-menu"
              PaperProps={menuPaperProps}
              anchorEl={libraryAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(libraryAnchorEl)}
              onClose={handleLibraryClose}
            >
              <MenuItem onClick={() => { handleLibraryClose(); navigate('/supplements'); }}>Supplements</MenuItem>
              <MenuItem onClick={() => { handleLibraryClose(); navigate('/conditions'); }}>Conditions</MenuItem>
              <MenuItem onClick={() => { handleLibraryClose(); navigate('/supplement-interaction-checker'); }}>Interactions</MenuItem>
            </Menu>
            </Box>
            {/* Right auth group — pinned to the right edge; its flexGrow
                balances the logo's so the center nav stays centered. */}
            <Box sx={{ ...navGroupSx, flex: '1 1 0', justifyContent: 'flex-end' }}>
            {currentUser ? (
              <>
              </>
            ) : (
              <>
                <Button color="inherit" onClick={() => navigate('/login')}>Log in</Button>
                <Button
                  onClick={() => navigate('/register')}
                  disableElevation
                  sx={{
                    ml: '0.375rem',
                    backgroundColor: `${LC.accent} !important`,
                    color: `${LC.accentInk} !important`,
                    fontWeight: '700 !important',
                    borderRadius: `${r(11)} !important`,
                    px: '1.125rem !important',
                    whiteSpace: 'nowrap',
                    '&:hover': { backgroundColor: `${LC.accentHover} !important` },
                  }}
                >
                  Start free trial
                </Button>
              </>
            )}
            {currentUser && (
              <>
                <IconButton
                  edge="end"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <AccountCircle />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  PaperProps={menuPaperProps}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={handleProfile}>Profile</MenuItem>
                  <MenuItem onClick={handleSupport}>Contact Us</MenuItem>
                  <MenuItem onClick={handleLogout}>Log Out</MenuItem>
                </Menu>
              </>
            )}
            </Box>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Header;